import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { hasRole, useApi, useApp } from "../context/AppContext";
import { nameOf, useProductNames } from "../lib/products";
import { PurchaseDetail } from "./PurchasesPage";

// One door opening (docs/tasks/35, level O): the purchase record and its
// evidence, every cloud check of the visit, and the cabinet's own timeline of
// it from the pipeline log (a window of the last 500 lines: an old visit may
// have fallen out of it).

const HOW = {
  classifier: "the classifier named it", agreed: "the classifier agreed", weight: "a weight tray counted it (never re-read)",
  weak_drop: "dropped: the classifier named another product", weak_kept: "kept", unsure: "unsure, left as it was",
  unknown_to_classifier: "a product the classifier was never trained on: left as it was", vlm: "the language model decided",
};

function CloudChecks({ sessionId, names }) {
  const api = useApi();
  const [data, setData] = useState(undefined);
  useEffect(() => { api.getSessionCloudChecks(sessionId).then(setData).catch((e) => setData({ error: String(e.message || e) })); }, [api, sessionId]);
  if (data === undefined) return null;
  if (data.error) return <div className="card text-xs text-rose-700">Cloud checks: {data.error}</div>;
  const checks = data.checks || [];
  return (
    <div className="card">
      <h2 className="mb-2 text-sm font-semibold">Cloud checks</h2>
      {checks.length === 0 ? <p className="text-sm text-slate-500">The cloud did not check this visit (nothing qualified, or the mode is off).</p> : checks.map((c) => {
        const a = c.answer || {};
        const told = c.adjust || c.report;
        return (
          <div key={c.request_id} className="mb-3 border-b border-slate-100 pb-2 text-sm last:border-0">
            <div className="font-medium">
              {c.kind === "dispute" ? "Customer dispute" : "Automatic check"} · {a.decision || (c.report ? "reported" : "—")}{a.changed ? " · the cart changed" : a.decision ? " · the cart stands" : ""}
              <span className="ml-2 font-mono text-xs text-slate-500">{c.request_id}</span>
            </div>
            {(a.lines || []).length > 0 && (
              <ul className="mt-1 text-xs">
                {a.lines.map((l, i) => {
                  const vote = l.detail?.classifier || (l.detail?.winner ? l.detail : null);
                  return (
                    <li key={i}>
                      {nameOf(names, l.product_uuid)}{l.pick && l.pick !== l.product_uuid ? ` → ${nameOf(names, l.pick)}` : ""}: {HOW[l.how] || l.how}
                      {l.confidence ? ` (${l.confidence})` : ""}
                      {vote?.winner ? <span className="text-slate-500"> · vote {nameOf(names, vote.winner)}, margin {Number(vote.margin).toFixed(2)}, {vote.n} crops</span> : null}
                      {l.detail?.vlm ? <span className="text-slate-500"> · language model (shadow): {l.detail.vlm.id ? nameOf(names, l.detail.vlm.id) : l.detail.vlm.decision || "abstained"}</span> : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {told && <p className="text-xs text-slate-600">Sales platform: {c.adjust ? "adjust" : "record"} → HTTP {told.status}{told.body?.result ? ` · ${told.body.result}` : ""}{told.body?.error ? ` · ${typeof told.body.error === "string" ? told.body.error : JSON.stringify(told.body.error)}` : ""}</p>}
            {a.evidence && <p className="font-mono text-[11px] text-slate-500">{a.evidence}</p>}
          </div>
        );
      })}
    </div>
  );
}

function Timeline({ deviceId, sessionId }) {
  const api = useApi();
  const { user } = useApp();
  const [lines, setLines] = useState(null);
  const may = hasRole(user, "tenant_admin", "device_operator");
  useEffect(() => {
    if (!may) return;
    api.getDevicePipelineLog(deviceId, { limit: 500 }).then((d) => {
      const entries = Array.isArray(d) ? d : d?.entries || [];
      setLines(entries.filter((e) => e.session === sessionId || e.session_id === sessionId || String(e.opening || "").startsWith(sessionId)));
    }).catch(() => setLines([]));
  }, [api, deviceId, sessionId, may]);
  if (!may) return null;
  return (
    <div className="card overflow-x-auto">
      <h2 className="mb-2 text-sm font-semibold">What the cabinet logged</h2>
      {lines === null ? <p className="text-sm text-slate-500">Loading…</p> : lines.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing of this visit is left in the cabinet's last 500 log lines.</p>
      ) : (
        <ul className="font-mono text-[11px]">
          {lines.map((e, i) => {
            const { ts, stage, session, session_id: _s, ...rest } = e;
            return (
              <li key={i} className="border-b border-slate-100 py-0.5">
                <span className="text-slate-500">{ts ? new Date(ts * 1000).toLocaleTimeString() : ""}</span>{" "}
                <span className="font-semibold">{stage}</span>{" "}
                <span className="text-slate-600">{JSON.stringify(rest).slice(0, 400)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function OpeningPage() {
  const { deviceId, sessionId } = useParams();
  const api = useApi();
  const names = useProductNames(api);
  const [cabinet, setCabinet] = useState(null);
  useEffect(() => { api.getDeviceHealth(deviceId).then(setCabinet).catch(() => setCabinet(null)); }, [api, deviceId]);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500">
          <Link className="hover:underline" to="/">Fleet</Link> › <Link className="hover:underline" to={`/cabinets/${deviceId}`}>{cabinet?.name || deviceId}</Link> › opening
        </div>
        <h1 className="text-2xl font-semibold">Opening</h1>
        <p className="font-mono text-xs text-slate-500">{sessionId}</p>
      </div>
      <div className="card"><PurchaseDetail sessionId={sessionId} names={names} deviceName={() => cabinet?.name || deviceId} link={false} /></div>
      <CloudChecks sessionId={sessionId} names={names} />
      <Timeline deviceId={deviceId} sessionId={sessionId} />
    </div>
  );
}
